import React from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  FaFileImage,
  FaImages,
  FaInfoCircle,
  FaPhotoVideo,
  FaSave,
  FaUpload,
} from "react-icons/fa";

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

function SectionCard({ eyebrow, title, icon, description, children }) {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-5">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            {eyebrow}
          </p>

          <h3 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
            <span className="text-indigo-600 dark:text-indigo-300">{icon}</span>
            {title}
          </h3>

          {description ? (
            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {description}
            </p>
          ) : null}
        </div>

        {children}
      </div>
    </section>
  );
}

function HeaderImageEditor({
  index,
  image,
  urlName,
  textName,
  settings,
  onChange,
  onContentChange,
  onOpenUpload,
}) {
  return (
    <SectionCard
      eyebrow="Header"
      title={`Image ${index}`}
      icon={<FaFileImage />}
      description="Image de fond du slider d’accueil avec texte superposé."
    >
      <div className="grid gap-4 xl:grid-cols-[180px,1fr]">
        <div>
          <div className="h-28 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
            <img
              src={image.src}
              alt={image.label}
              className="h-full w-full object-cover"
              onError={(event) => {
                event.currentTarget.src = image.fallback;
              }}
            />
          </div>

          <button
            type="button"
            onClick={() => onOpenUpload(image.id)}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6d] dark:bg-indigo-500 dark:hover:bg-indigo-400"
          >
            <FaUpload />
            Téléverser
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Lien de l’image
            </label>

            <input
              type="text"
              name={urlName}
              value={settings[urlName] || ""}
              onChange={onChange}
              placeholder="https://... ou /uploads/..."
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Texte superposé
            </label>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
              <ReactQuill
                value={settings[textName] || ""}
                onChange={(value) => onContentChange(textName, value)}
                modules={modules}
                formats={formats}
                placeholder="<h1>Votre titre</h1><p>Votre sous-titre</p>"
              />
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

export default function HomeContentForm({
  formId,
  settings,
  galleries,
  headerImages,
  selectedGallery,
  loading,
  saving,
  onChange,
  onContentChange,
  onSubmit,
  onOpenUpload,
}) {
  if (loading) {
    return (
      <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />
        <div className="space-y-3 p-5">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="h-16 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <form id={formId} onSubmit={onSubmit} className="space-y-5">
      <HeaderImageEditor
        index={1}
        image={headerImages[0]}
        urlName="header_url1"
        textName="header_texte1"
        settings={settings}
        onChange={onChange}
        onContentChange={onContentChange}
        onOpenUpload={onOpenUpload}
      />

      <HeaderImageEditor
        index={2}
        image={headerImages[1]}
        urlName="header_url2"
        textName="header_texte2"
        settings={settings}
        onChange={onChange}
        onContentChange={onContentChange}
        onOpenUpload={onOpenUpload}
      />

      <SectionCard
        eyebrow="Présentation"
        title="Section À propos"
        icon={<FaInfoCircle />}
        description="Texte de présentation affiché sous le header de la page d’accueil. Ce texte est important pour le référencement naturel du site, n’hésitez pas à le détailler et à y intégrer des mots-clés pertinents sur votre club et vos activités."
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Titre
            </label>

            <input
              type="text"
              name="about_title"
              value={settings.about_title || ""}
              onChange={onChange}
              placeholder="Qui sommes-nous ?"
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 outline-none transition placeholder:text-slate-400 focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Contenu
            </label>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950 quill-wrapper">
              <ReactQuill
                value={settings.about_content || ""}
                onChange={(value) => onContentChange("about_content", value)}
                modules={modules}
                formats={formats}
                placeholder="<p>Notre club...</p>"
              />
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        eyebrow="Photos"
        title="Carrousel d’accueil"
        icon={<FaPhotoVideo />}
        description="Choisissez l’album photo (si disponible) à afficher sur la page d’accueil."
      >
        {galleries.length > 0 ? (
          <select
            name="carrousel_id"
            value={settings.carrousel_id || ""}
            onChange={(event) => {
              const value = event.target.value === "" ? null : event.target.value;
              onChange({
                target: {
                  name: "carrousel_id",
                  value,
                },
              });
            }}
            className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
          >
            <option value="">Ne pas afficher d’album</option>
            {galleries.map((gallery) => (
              <option key={gallery.id} value={gallery.id}>
                {gallery.titre}
              </option>
            ))}
          </select>
        ) : (
          <p className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-400 dark:ring-slate-800">
            Aucun album photo disponible.
          </p>
        )}

        {settings.carrousel_id ? (
          <p className="mt-3 rounded-2xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
            Album sélectionné : {selectedGallery?.titre || `#${settings.carrousel_id}`}
          </p>
        ) : null}
      </SectionCard>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#012f6d] disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
        >
          <FaSave />
          {saving ? "Enregistrement..." : "Mettre à jour"}
        </button>
      </div>

      <style>{`
        .quill-wrapper .ql-toolbar.ql-snow {
          border: 0;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
        }

        .quill-wrapper .ql-container.ql-snow {
          border: 0;
          min-height: 180px;
        }

        .quill-wrapper .ql-editor {
          min-height: 180px;
          font-size: 14px;
          line-height: 1.7;
        }

        .dark .quill-wrapper .ql-toolbar.ql-snow {
          background: rgba(15,23,42,.98);
          border-color: #334155;
        }

        .dark .quill-wrapper .ql-container.ql-snow {
          background: rgba(2,6,23,.7);
        }

        .dark .quill-wrapper .ql-editor {
          color: #e2e8f0;
        }

        .dark .ql-snow .ql-stroke {
          stroke: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-fill {
          fill: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker,
        .dark .ql-snow .ql-picker-label,
        .dark .ql-snow .ql-picker-options,
        .dark .ql-snow .ql-toolbar button {
          color: #e2e8f0 !important;
        }

        .dark .ql-snow .ql-picker-options {
          background: #0b1220;
          border-color: #334155;
        }
      `}</style>
    </form>
  );
}